import { LayoutOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import * as Sentry from '@sentry/browser'
import { TemplateHint } from '@ulysses-inc/harami_api_client'
import { Breadcrumb, Button, Layout, notification } from 'antd'
import { Location } from 'history'
import { useCallback, useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Link, Prompt, useHistory } from 'react-router-dom'
import { AnyAction, Dispatch } from 'redux'
import { Header } from 'src/components/header/Header'
import Loading from 'src/components/loading/Loading'
import LoadingOverlay from 'src/components/loading/LoadingOverlay'
import DataUnavailableWidget from 'src/features/dataUnavailableWidget/DataUnavailableWidget'
import { useDataUnavailableWidget } from 'src/features/dataUnavailableWidget/useDataUnavailableWidget'
import { TableSectionSoftLimitErrorModal } from 'src/features/templateEdit/errorModal/TableSectionSoftLimitErrorModal'
import { extractUniqueMultipleChoiceSets } from 'src/features/templateEdit/extractUniqueMultipleChoiceSets'
import { normalizeTableSections } from 'src/features/templateEdit/section/table/model'
import { TemplateNodeSoftLimitDialog } from 'src/features/templateEdit/TemplateNodeSoftLimitDialog'
import EditTemplateOption from 'src/features/templateEdit/templateOptions/EditTemplateOption'
import EditTemplatePages from 'src/features/templateEdit/templatePages/EditTemplatePages'
import { useTemplateNodeCapacityAlert } from 'src/features/templateEdit/useTemplateNodeCapacityAlert'
import { useTemplateNodeSoftLimit } from 'src/features/templateEdit/useTemplateNodeSoftLimit'
import { validateAndDispatch as runValidateAndDispatch } from 'src/features/templateEdit/validates/validateAndDispatch'
import {
  collectTableSectionSoftLimitViolations,
  type TableSectionSoftLimitViolation,
} from 'src/features/templateEdit/validates/validateTableSectionSoftLimit'
import { useQueryTraceKeys } from 'src/features/traceKeys/api/useQueryTraceKeys'

import { showNotification } from 'src/features/notificationService/slice'
import {
  buildCreateTemplateV3Body,
  buildUpdateTemplateV3Body,
} from 'src/features/templateEdit/api/buildTemplateV3Body'
import { useMutationCreateTemplateV3 } from 'src/features/templateEdit/api/useMutationCreateTemplateV3'
import { useMutationUpdateTemplateV3 } from 'src/features/templateEdit/api/useMutationUpdateTemplateV3'
import interceptionsActions from 'src/state/ducks/interceptions/actions'
import { getMultipleChoiceSets } from 'src/state/ducks/multipleChoiceSets/actions'
import {
  getProductMasterFieldDefsRequested,
  productMasterFieldDefsReset,
} from 'src/state/ducks/productMasterFieldDefs/slice'
import {
  addSuccessTemplatePages,
  getTemplatePagesWithRule,
  resetTemplatePages,
  setTemplatePageErrorMessage,
} from 'src/state/ducks/templates/actions'
import {
  addErrorTemplate,
  addSuccessTemplate,
  getEmptyTemplate,
  getPreparedTemplate,
  getTemplate,
  resetTemplate,
  updateErrorTemplate,
  updateSuccessTemplate,
} from 'src/state/ducks/templates/editTemplate/actions'
import { EditTemplateState } from 'src/state/ducks/templates/editTemplate/reducer'
import {
  addSuccessTemplateHints,
  getTemplateHints,
  resetTemplateHints,
} from 'src/state/ducks/templates/templateHints/actions'
import { TemplatePagesState } from 'src/state/ducks/templates/templatePages/reducer'
import { getUserGroups } from 'src/state/ducks/users/actions'
import { RootState } from 'src/state/store'
import { ExhaustiveError } from 'src/util/error'
import {
  TemplateEditModeProvider,
  useTemplateEditMode,
} from './context/TemplateEditMode/TemplateEditMode'
import { ValidateParams, ValidationError } from './validates/types'

const validate = (
  dispatch: Dispatch<AnyAction>,
  params: ValidateParams,
): true | ValidationError['type'] => {
  return runValidateAndDispatch(dispatch, params)
}

export const EditTemplate: React.FC = () => {
  return (
    <TemplateEditModeProvider>
      <EditTemplateInner />
    </TemplateEditModeProvider>
  )
}

const EditTemplateInner = () => {
  const dispatch = useDispatch()

  const {
    editTemplate: templateData, // ひな形関係
    templatePages: templatePagesData, // ひな形ページ関係
    templateHints: templateHintsData, // ひな形ヒント関係
  } = useSelector((state: RootState) => state.templatesState)

  const isDirty = useSelector((state: RootState) => {
    return (
      state.templatesState.editTemplate.isDirty ||
      state.templatesState.templatePages.isDirty ||
      state.templatesState.templateHints.isDirty
    )
  })

  const createMutation = useMutationCreateTemplateV3()
  const updateMutation = useMutationUpdateTemplateV3()
  const isSaving = createMutation.isPending || updateMutation.isPending

  const templateEditMode = useTemplateEditMode()

  const history = useHistory()

  // 新規作成成功時に一覧画面へ遷移する。
  // onSuccess 内で直接 history.push すると、React 再レンダリング前に Prompt がブロックするため、
  // useEffect で createMutation.isSuccess を監視して遷移する。
  useEffect(() => {
    if (createMutation.isSuccess) {
      history.push('/templates')
    }
  }, [createMutation.isSuccess, history])

  const dataUnavailableWidget = useDataUnavailableWidget()

  // 表セクションのソフトリミット超過エラーダイアログの表示制御
  const [tableSectionSoftLimitViolations, setTableSectionSoftLimitViolations] =
    useState<TableSectionSoftLimitViolation[]>([])

  const templateNodeSoftLimit = useTemplateNodeSoftLimit()
  useTemplateNodeCapacityAlert()

  useEffect(() => {
    switch (templateEditMode.mode) {
      case 'new':
        dispatch(getEmptyTemplate())
        break
      case 'newFromPreparedTemplate':
        dispatch(
          getPreparedTemplate(
            templateEditMode.templateId,
            templateEditMode.key,
          ),
        )
        break
      case 'edit':
        dispatch(getTemplatePagesWithRule(templateEditMode.templateId))
        dispatch(getTemplateHints(templateEditMode.templateId))
        dispatch(getTemplate(templateEditMode.templateId))
        break
    }

    dispatch(getUserGroups({}))
    dispatch(getMultipleChoiceSets({}))

    return () => {
      dispatch(resetTemplate())
      dispatch(resetTemplatePages())
      dispatch(resetTemplateHints())
      dataUnavailableWidget.reset()
      dispatch(productMasterFieldDefsReset())
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const overlayTip =
    templateEditMode.mode === 'edit'
      ? 'ひな形を更新中です...'
      : 'ひな形を登録中です...'

  const isInitialLoading =
    templateHintsData.isLoading || templatePagesData.isLoading

  const masterFieldDefs = useSelector(
    (state: RootState) =>
      state.productMasterFieldDefsState.productMasterFieldDefs,
    shallowEqual,
  )

  const masterUuid = useSelector(
    (state: RootState) => state.templatesState.editTemplate.masterUuid,
  )

  // masterUuid が変更されたら fieldDefs を再取得する
  useEffect(() => {
    if (masterUuid) {
      dispatch(getProductMasterFieldDefsRequested(masterUuid))
    } else {
      dispatch(productMasterFieldDefsReset())
    }
  }, [dispatch, masterUuid])

  const { data: traceKeys = [] } = useQueryTraceKeys()
  const saveTemplate = (
    templateData: EditTemplateState,
    templateHints: TemplateHint[],
    templatePagesData: TemplatePagesState,
  ) => {
    // 表セクションを正規化する。throw は表の同期のバグなので、保存を中断して通知する。
    let templatePagesDataForSave: TemplatePagesState
    try {
      templatePagesDataForSave = {
        ...templatePagesData,
        templateNodes: normalizeTableSections(templatePagesData.templateNodes),
      }
    } catch (error) {
      Sentry.captureException(error)
      notification.error({
        message: 'ひな形の保存に失敗しました。',
        description:
          '表セクションのデータに問題があります。ページを再読み込みして再度お試しください。',
      })
      return
    }

    // 表セクション（sectionType = TABLE）のソフトリミットを
    // 静的に検証する（即死対策）。上限超過があれば保存を中断し、エラーダイアログを表示する。
    const tableSectionViolations = collectTableSectionSoftLimitViolations(
      templatePagesDataForSave.templateNodes,
    )
    if (tableSectionViolations.length > 0) {
      setTableSectionSoftLimitViolations(tableSectionViolations)
      return
    }

    const validationResult = validate(dispatch, {
      templateData,
      templateHints,
      templatePagesData: templatePagesDataForSave,
      masterFieldDefs,
      traceKeys,
    })

    if (validationResult !== true) {
      notification.error({
        message: 'ひな形の保存に失敗しました。',
        description: 'エラー箇所を確認して修正してください。',
        duration: 2,
      })

      // Reactのレンダリング完了を待ってからスクロール
      requestAnimationFrame(() => {
        // エラータイプに応じてスクロール先を決定
        let targetElementId: string

        // master だけ異なるのは、 MUI 化によってエラーメッセージの表示方法を変更しているため
        if (validationResult === 'master') {
          targetElementId = 'master-selector-error'
        } else {
          targetElementId = 'error-message'
        }

        const errorElement = document.getElementById(targetElementId)
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      })

      return
    }

    const multipleChoiceSets = extractUniqueMultipleChoiceSets(
      templatePagesDataForSave.templateNodes,
    )
    const templateArgs = {
      templatePages: templatePagesDataForSave.templatePages,
      templateNodes: templatePagesDataForSave.templateNodes,
      templatePageIds: templatePagesDataForSave.templatePageIds,
      templateHints: templateHints,
      name: templateData.name,
      isShowScore: templateData.isShowScore,
      isEditable: templateData.isEditable,
      isExcelConversion: templateData.isExcelConversion,
      isHideQuestionOptions: templateData.isHideQuestionOptions,
      isAudit: templateData.isAudit,
      isKeyboard: templateData.isKeyboard,
      layoutType: templateData.layoutType,
      hasVariables: templateData.hasVariables,
      manuals: templateData.manuals,
      icons: templateData.icons,
      approvalFlowId: templateData.approvalFlowId,
      placeNodes: templateData.selectedPlaceNodes,
      multipleChoiceSets,
      templateReportNamingRulesTemplateNodeUuid:
        templatePagesDataForSave.templateReportNamingRulesTemplateNodeUuid,
      masterUuid: templateData.masterUuid,
    }
    switch (templateEditMode.mode) {
      case 'edit':
        updateMutation.mutate(
          {
            templateId: templateEditMode.templateId,
            body: buildUpdateTemplateV3Body(templateArgs),
          },
          {
            onSuccess: () => {
              dispatch(updateSuccessTemplate())
              dispatch(addSuccessTemplatePages())
              dispatch(addSuccessTemplateHints())
              dispatch(setTemplatePageErrorMessage(''))
              dispatch(
                showNotification({ message: 'ひな形の更新に成功しました' }),
              )
            },
            onError: error => {
              dispatch(interceptionsActions.handleHttpError(error))
              dispatch(updateErrorTemplate(error))
            },
          },
        )
        break
      case 'new':
      case 'newFromPreparedTemplate':
        createMutation.mutate(buildCreateTemplateV3Body(templateArgs), {
          onSuccess: _data => {
            // _data.id で作成されたひな形の ID を参照可能（将来の拡張用）
            dispatch(addSuccessTemplate())
            dispatch(addSuccessTemplatePages())
            dispatch(addSuccessTemplateHints())
            dispatch(
              showNotification({ message: 'ひな形の作成に成功しました' }),
            )
            // 遷移は useEffect で createMutation.isSuccess を監視して行う
          },
          onError: error => {
            dispatch(interceptionsActions.handleHttpError(error))
            dispatch(addErrorTemplate(error))
          },
        })
        break
      default:
        throw new ExhaustiveError(templateEditMode)
    }
  }

  const messageCallback = useCallback((location: Location) => {
    const message = '変更した内容が破棄されますがよろしいですか？'

    // NOTE: キャンセルしたときにURLがもとに戻らないバグがある
    if (location.pathname === '/templates') {
      return JSON.stringify({
        message,
        state: {
          data: window.history.state,
          title: document.title,
          url: window.location.href,
        },
      })
    }
    return message
  }, [])

  return (
    <>
      <LoadingOverlay
        tip={overlayTip}
        spinning={isSaving}
        size="large"
        render={
          <>
            <Header
              rightSlot={[
                <Button
                  key="1"
                  type="primary"
                  onClick={() => {
                    saveTemplate(
                      templateData,
                      templateHintsData.templateHints,
                      templatePagesData,
                    )
                  }}
                  disabled={
                    dataUnavailableWidget.isVisible ||
                    templateNodeSoftLimit.isOverLimit
                  }
                >
                  保存
                </Button>,
              ]}
              sticky
            >
              <Breadcrumb css={styles.breadcrumb}>
                <Breadcrumb.Item>
                  <Link to="/templates">
                    <LayoutOutlined css={styles.layoutIcon} />
                    ひな形一覧
                  </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  ひな形
                  {templateEditMode.mode === 'edit' ? 'の変更' : 'の追加'}
                </Breadcrumb.Item>
              </Breadcrumb>
            </Header>
            {dataUnavailableWidget.isVisible ? (
              <DataUnavailableWidget />
            ) : (
              <Layout className="layout">
                <Prompt
                  when={isDirty && !createMutation.isSuccess}
                  message={messageCallback}
                />
                <Layout.Content>
                  {isInitialLoading ? (
                    <Loading />
                  ) : (
                    <>
                      <EditTemplateOption />
                      <EditTemplatePages />
                    </>
                  )}
                </Layout.Content>
              </Layout>
            )}
          </>
        }
      />
      <TableSectionSoftLimitErrorModal
        open={tableSectionSoftLimitViolations.length > 0}
        violations={tableSectionSoftLimitViolations}
        onClose={() => setTableSectionSoftLimitViolations([])}
      />
      <TemplateNodeSoftLimitDialog />
    </>
  )
}

const styles = {
  breadcrumb: css`
    font-weight: bold;
  `,
  layoutIcon: css`
    margin-right: 5px;
  `,
}
